import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { Comment } from "../types/comment";
import { Deficiency, DeficiencyType } from "../types/deficiencies";
import { MinimalProduct } from "../types/product";

export type Attachment = {
  type: "Incident" | "Appointment";
  id: string;
};

export type EmailProps = {
  id: string;
  email: string;
};

export type SignatureProps = {
  url: string;
};

export type Technician = {
  firstName: string;
  lastName?: string;
  profileName?: string;
};

export type AppointmentSubset = {
  customerName: string;
  address: string;
  street: string;
  postcode: string;
  city: string;
  objectName: string | null;
  technicians: Technician[];
  typeOfAppointment: "monitoring" | "auftrag" | "other" | null;
};

type DeficiencyPDF = {
  deficiencyType: DeficiencyType | null;
  date: string;
  time: string;
  location: string;
  responsibility: string;
  requiredActions?: string[];
  additionalText?: string;
  state: string;
  actions?: string[];
  commentsList: Comment[];
  processedBy?: {
    id: string;
    type: string;
    user: string;
  };
  processedAt?: string;
};

export type Event = {
  type: string;
  count: number;
};

export type Monitor = {
  monitorType: string;
  events: Event[];
};

export type PropertyArea = {
  area: string;
  propertyAreaId: string;
  events: Monitor[];
};

export type PropertyAreaDeficiencies = {
  area: string;
  incidents: Deficiency[];
};

export type PropertyAreaAdjustedMonitors = {
  label: string;
  monitors: Monitor[];
};

export type DocumentationSliceState = {
  emailOwner: string;
  signature: SignatureProps;
  emails: EmailProps[];
  remark: string;
  appointmentSubset: AppointmentSubset;
  date: string;
  totalTimeWorkload: string;
  products: MinimalProduct[];
  propertyAreas: PropertyArea[];
  propertyAreasDeficiencies: PropertyAreaDeficiencies[];
  existingReceipt: boolean;
  existingReceiptId: string;
  defAppointmentId: string | undefined;
  defJobId: string | undefined;
  attachedTo: Attachment;
  propertyAreaAdjustedMonitors: PropertyAreaAdjustedMonitors[];
  deficiencyPDF: DeficiencyPDF;
  showCommentsInPDF: boolean;
  showPhotosInPDF: boolean;
  roomRelease: string | null;
  measureRequired: string | null;
};

const initialState: DocumentationSliceState = {
  emailOwner: "",
  signature: {
    url: "",
  },
  emails: [],
  remark: "",
  appointmentSubset: {
    customerName: "",
    address: "",
    street: "",
    postcode: "",
    city: "",
    objectName: null,
    technicians: [],
    typeOfAppointment: null,
  },
  date: "",
  totalTimeWorkload: "",
  products: [],
  propertyAreas: [],
  propertyAreasDeficiencies: [],
  existingReceipt: false,
  existingReceiptId: "",
  defAppointmentId: undefined,
  defJobId: undefined,
  attachedTo: {
    type: "Appointment",
    id: "",
  },
  propertyAreaAdjustedMonitors: [],
  deficiencyPDF: {
    deficiencyType: null,
    date: "",
    time: "",
    location: "",
    responsibility: "",
    requiredActions: [],
    additionalText: "",
    state: "",
    commentsList: [],
    processedBy: {
      id: "",
      type: "",
      user: "",
    },
    processedAt: "",
  },
  showCommentsInPDF: false,
  showPhotosInPDF: true,
  roomRelease: null,
  measureRequired: null,
};

export const creatingDocumentationSlice = createSlice({
  name: "documentation",
  initialState: initialState,
  reducers: {
    setEmailOwner(state: DocumentationSliceState, action: { payload: string }) {
      state.emailOwner = action.payload;
    },
    setSignatureImage(state, action: PayloadAction<SignatureProps>) {
      state.signature = action.payload;
    },
    setRemark(state, action: PayloadAction<string>) {
      state.remark = action.payload;
    },
    setAppointmentSubset(state, action: PayloadAction<AppointmentSubset>) {
      state.appointmentSubset = action.payload;
    },
    setDate(state, action: PayloadAction<string>) {
      state.date = action.payload;
    },
    setTotalTimeWorkload(state, action: PayloadAction<string>) {
      state.totalTimeWorkload = action.payload;
    },
    addEmail(state, action: PayloadAction<EmailProps>) {
      const emailIndex = state.emails.findIndex(
        (email) => email.id === action.payload.id
      );

      if (emailIndex !== -1) {
        state.emails[emailIndex] = action.payload;
      } else {
        state.emails.push(action.payload);
      }
    },
    setProducts(state, action: PayloadAction<MinimalProduct[]>) {
      state.products = action.payload;
    },
    setPropertyAreaEvents(state, action: PayloadAction<PropertyArea[]>) {
      state.propertyAreas = action.payload;
    },
    setPropertyAreaDeficiencies(
      state,
      action: PayloadAction<PropertyAreaDeficiencies[]>
    ) {
      state.propertyAreasDeficiencies = action.payload;
    },
    setPropertyAreaAdjustedMonitors(
      state,
      action: PayloadAction<PropertyAreaAdjustedMonitors[]>
    ) {
      state.propertyAreaAdjustedMonitors = action.payload;
    },
    setExistingReceipt(state, action: PayloadAction<boolean>) {
      state.existingReceipt = action.payload;
    },
    setExistingReceiptId(state, action: PayloadAction<string>) {
      state.existingReceiptId = action.payload;
    },
    setDefAppointment(state, action: PayloadAction<string>) {
      state.defAppointmentId = action.payload;
    },
    setDefJob(state, action: PayloadAction<string>) {
      state.defJobId = action.payload;
    },
    removeEmail(state, action: PayloadAction<string>) {
      state.emails = state.emails.filter(
        (email) => email.email !== action.payload
      );
    },
    setAttachedTo(state, action: PayloadAction<Attachment>) {
      state.attachedTo = action.payload;
    },
    setDeficiencyPDF(state, action: PayloadAction<DeficiencyPDF>) {
      state.deficiencyPDF = action.payload;
    },
    setShowCommentsInPDF(state, action: PayloadAction<boolean>) {
      state.showCommentsInPDF = action.payload;
    },
    setShowPhotos(state, action: PayloadAction<boolean>) {
      state.showPhotosInPDF = action.payload;
    },
    setRoomRelease(state, action: PayloadAction<string>) {
      state.roomRelease = action.payload;
    },
    setMeasureRequired(state, action: PayloadAction<string>) {
      state.measureRequired = action.payload;
    },
    resetEmails(state) {
      state.emails = [];
    },
    resetState(state: DocumentationSliceState) {
      return { ...initialState };
    },
  },
});

export const creatingDocumentationActions = creatingDocumentationSlice.actions;
